import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { getStripe } from "./stripe.server";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { getSiteOrigin } from "@/lib/site-origin.server";
import { checkRateLimit, getClientIp } from "@/lib/rate-limit.server";

const COURSE_PATH =
  "/courses/business-and-accountancy/basic-business-accountancy";

/**
 * Create a Stripe Checkout Session and return the URL to redirect the user to.
 */
export const createCheckoutSession = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        email: z.string().trim().email().max(255).optional(),
        // Per-page-load nonce from the client. Lets us dedupe anonymous
        // double-clicks (same nonce + 5-min bucket = same Stripe session)
        // while a reload / new mount generates a fresh nonce.
        clientNonce: z.string().uuid().optional(),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    // Rate-limit gate 1: per-IP. Defends Stripe API quota.
    const ip = getClientIp();
    const ipCheck = await checkRateLimit({
      name: "checkout-ip",
      identifier: ip,
      windowSeconds: 60,
      maxRequests: 5,
    });
    if (!ipCheck.allowed) {
      return {
        ok: false as const,
        error: "rate_limited" as const,
        retryAfterSeconds: ipCheck.retryAfterSeconds,
      };
    }

    // Rate-limit gate 2: per-email (when supplied — most checkouts are
    // anonymous so this is best-effort).
    if (data.email) {
      const emailCheck = await checkRateLimit({
        name: "checkout-email",
        identifier: data.email.toLowerCase().trim(),
        windowSeconds: 60,
        maxRequests: 3,
      });
      if (!emailCheck.allowed) {
        return {
          ok: false as const,
          error: "rate_limited" as const,
          retryAfterSeconds: emailCheck.retryAfterSeconds,
        };
      }
    }

    const priceId = process.env.STRIPE_PRICE_ID;
    if (!priceId) {
      console.error("[stripe] STRIPE_PRICE_ID is not configured");
      return { ok: false as const, error: "config" as const };
    }
    try {
      const stripe = getStripe();
      const origin = getSiteOrigin();
      // Deterministic idempotency key. Priority:
      //   1. email present → email + bucket (dedupes across tabs/devices)
      //   2. clientNonce   → nonce + bucket (dedupes same page-load)
      //   3. neither       → randomUUID (no dedupe; legacy fallback)
      const bucket = Math.floor(Date.now() / (5 * 60 * 1000));
      const keySource = data.email
        ? `checkout:v1:${data.email.toLowerCase()}:${priceId}:${bucket}`
        : data.clientNonce
          ? `checkout:v1:nonce:${data.clientNonce}:${priceId}:${bucket}`
          : `checkout:v1:anon:${priceId}:${bucket}:${crypto.randomUUID()}`;
      const keyBytes = await crypto.subtle.digest(
        "SHA-256",
        new TextEncoder().encode(keySource),
      );
      const idempotencyKey = Array.from(new Uint8Array(keyBytes))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
      const session = await stripe.checkout.sessions.create(
        {
          mode: "payment",
          line_items: [{ price: priceId, quantity: 1 }],
          success_url: `${origin}/success?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${origin}${COURSE_PATH}`,
          customer_email: data.email,
          allow_promotion_codes: true,
          billing_address_collection: "auto",
        },
        { idempotencyKey },
      );
      console.log("[stripe] createCheckoutSession result", {
        idempotencyKey,
        sessionId: session.id,
        sessionUrl: session.url,
      });
      if (!session.url) {
        return { ok: false as const, error: "server" as const };
      }
      return { ok: true as const, url: session.url };
    } catch (err) {
      console.error("[stripe] createCheckoutSession failed:", err);
      return { ok: false as const, error: "server" as const };
    }
  });

/**
 * Called from /success after Stripe redirect.
 * Validates the session was paid, then issues a magic-link token the client
 * can verify with supabase.auth.verifyOtp({ type: 'magiclink', token_hash }).
 */
export const confirmPaymentAndSignIn = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z.object({ session_id: z.string().min(1).max(255) }).parse(input),
  )
  .handler(async ({ data }) => {
    try {
      const stripe = getStripe();
      const session = await stripe.checkout.sessions.retrieve(data.session_id);

      const paid = session.payment_status === "paid";
      const email = (session.customer_details?.email ?? session.customer_email ?? "")
        .toString()
        .trim()
        .toLowerCase();

      if (!paid || !email) {
        return { ok: false as const, error: "not_paid" as const };
      }

      // Look up purchase row (created by the webhook). It may not have arrived yet.
      const { data: purchase } = await supabaseAdmin
        .from("purchases")
        .select("id, paid_at, user_id")
        .eq("stripe_session_id", session.id)
        .maybeSingle();

      // Generate a magic link token for sign-in.
      const { data: linkData, error: linkErr } = await supabaseAdmin.auth.admin.generateLink({
        type: "magiclink",
        email,
      });
      if (linkErr || !linkData) {
        console.error("[stripe] generateLink failed:", linkErr);
        return { ok: false as const, error: "auth" as const };
      }

      const tokenHash = linkData.properties?.hashed_token;
      if (!tokenHash) {
        return { ok: false as const, error: "auth" as const };
      }

      return {
        ok: true as const,
        email,
        token_hash: tokenHash,
        purchase_recorded: !!purchase,
      };
    } catch (err) {
      console.error("[stripe] confirmPaymentAndSignIn failed:", err);
      return { ok: false as const, error: "server" as const };
    }
  });
